import { MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { OfferType } from "../../models/EnginesWorldwide";
import { IVehicle } from "../../models/Vehicle";
import PriceFormat from "../../utils/priceFormat";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useSingleSetting from "../../data/useSingleSetting";

type EnginesWorldwideLookupProps = {
  vehicle: Pick<IVehicle, "id" | "enginesWorldwideEngineOffer" | "enginesWorldwideVfeOffer" | "enginesWorldwide">;
  variant?: "details" | "inline";
  showLookupButton?: boolean;
  inlineTitle?: string;
  className?: string;
  onUpdated?: (vehicle: IVehicle) => void;
};

const parseOffer = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatOffer = (value: string | number | null | undefined, naLabel: string) => {
  const parsed = parseOffer(value);
  return parsed !== null ? PriceFormat(parsed) : naLabel;
};

export default function EnginesWorldwideLookup({
  vehicle,
  variant = "details",
  showLookupButton = true,
  inlineTitle = "EWW",
  className = "",
  onUpdated,
}: EnginesWorldwideLookupProps) {
  const { t } = useTranslation("vehicles");
  const { enginesWorldwideRefresh } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Engines Worldwide lookup"));
  const [engineOffer, setEngineOffer] = useState<string | number | null>(vehicle.enginesWorldwideEngineOffer);
  const [vfeOffer, setVfeOffer] = useState<string | number | null>(vehicle.enginesWorldwideVfeOffer);
  const { singleSetting: ewwUsernameSetting } = useSingleSetting("EnginesWorldwide:Username");
  const { singleSetting: ewwPasswordSetting } = useSingleSetting("EnginesWorldwide:Password");
  useEffect(() => {
    setEngineOffer(vehicle.enginesWorldwideEngineOffer ?? vehicle.enginesWorldwide?.find((e) => e.type === OfferType.Engine)?.price ?? null);
    setVfeOffer(vehicle.enginesWorldwideVfeOffer ?? vehicle.enginesWorldwide?.find((e) => e.type === OfferType.Vfe)?.price ?? null);
  }, [vehicle.id, vehicle.enginesWorldwideEngineOffer, vehicle.enginesWorldwideVfeOffer, vehicle.enginesWorldwide]);

  const hasEwwCredentials =
    !!ewwUsernameSetting.data?.data?.value &&
    !!ewwPasswordSetting.data?.data?.value;
  const canRunLookup = showLookupButton && hasEwwCredentials;

  const handleLookup = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    enginesWorldwideRefresh.mutate(vehicle.id, {
      onSuccess: (response) => {
        setEngineOffer(response.data.enginesWorldwideEngineOffer);
        setVfeOffer(response.data.enginesWorldwideVfeOffer);
        onUpdated?.(response.data);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const offerTextClass = "text-sm font-medium text-red-500";

  if (variant === "inline") {
    return (
      <div className={`py-2 grid grid-cols-3 gap-4 ${className}`} onClick={(e) => e.stopPropagation()}>
        <dt className="text-sm font-medium text-gray-500">{inlineTitle}</dt>
        <dd className="flex justify-between items-center text-sm col-span-2">
          <span className={`flex flex-wrap items-center gap-x-3 min-w-0 ${offerTextClass}`}>
            <span>{t("Engine: {{offer}}", { offer: formatOffer(engineOffer, t("N/A")) })}</span>
            <span>{t("VFE: {{offer}}", { offer: formatOffer(vfeOffer, t("N/A")) })}</span>
          </span>
          {canRunLookup && (
            <ButtonInput
              label={t("Lookup EWW")}
              onClick={handleLookup}
              classes="px-1 py-1 mr-3 shrink-0"
              disabled={!vehicle.id}
              isLoading={enginesWorldwideRefresh.isLoading}
              size="sm"
            />
          )}
        </dd>
      </div>
    );
  }

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      <div className="py-2 grid grid-cols-3 gap-4">
        <dt className={offerTextClass}>{t("Engine Price")}</dt>
        <dd className={`${offerTextClass} col-span-2`}>{formatOffer(engineOffer, t("N/A"))}</dd>
      </div>
      <div className="py-2 grid grid-cols-3 gap-4">
        <dt className={offerTextClass}>{t("Front End")}</dt>
        <dd className={`${offerTextClass} col-span-2`}>{formatOffer(vfeOffer, t("N/A"))}</dd>
      </div>
      {canRunLookup && (
        <div className="py-2 grid grid-cols-3 gap-4">
          <dt className="text-sm font-medium text-gray-500">{t("Engines Worldwide")}</dt>
          <dd className="text-sm col-span-2">
            <ButtonInput
              label={t("Run Lookup")}
              onClick={handleLookup}
              classes=""
              disabled={!vehicle.id}
              isLoading={enginesWorldwideRefresh.isLoading}
              size="sm"
            />
          </dd>
        </div>
      )}
    </div>
  );
}
